import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, TrendingUp, Activity, BarChart3 } from 'lucide-react';
import { useCostCenters } from '@/hooks/useCostCenters';

interface CostCenterSummaryProps {
  className?: string;
}

export function CostCenterSummary({ className }: CostCenterSummaryProps) {
  const { costCenters, isLoading } = useCostCenters();

  if (isLoading) {
    return (
      <div className={`grid gap-4 md:grid-cols-2 lg:grid-cols-4 ${className}`}>
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                <div className="h-4 w-20 bg-blue-100 rounded animate-pulse"></div>
              </CardTitle>
              <div className="h-4 w-4 bg-blue-200 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-blue-100 rounded animate-pulse mb-2"></div>
              <div className="h-3 w-24 bg-blue-50 rounded animate-pulse"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const totalCenters = costCenters.length;
  const activeCenters = costCenters.length; // Por enquanto todos estão ativos
  const recentCenters = costCenters.filter(cc => {
    const createdAt = new Date(cc.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return createdAt >= thirtyDaysAgo;
  }).length;

  // Simulação de estatísticas - no futuro poderia vir de queries mais complexas
  const avgCentersPerUser = totalCenters > 0 ? Math.round((totalCenters / 1) * 10) / 10 : 0;

  return (
    <div className={`grid gap-4 md:grid-cols-2 lg:grid-cols-4 ${className}`}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total</CardTitle>
          <Building2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-900">{totalCenters}</div>
          <p className="text-xs text-muted-foreground">
            {totalCenters === 0 ? 'Nenhum centro cadastrado' : 'Centros de custo ativos'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Ativos</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activeCenters}</div>
          <p className="text-xs text-muted-foreground">
            {totalCenters > 0 ? `${Math.round((activeCenters / totalCenters) * 100)}% do total` : 'Nenhuma atividade'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Recentes</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{recentCenters}</div>
          <p className="text-xs text-muted-foreground">
            Criados nos últimos 30 dias
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Crescimento</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">+{recentCenters}</div>
          <p className="text-xs text-muted-foreground">
            Novos centros este mês
          </p>
        </CardContent>
      </Card>
    </div>
  );
}